// Settings → Admin → Recycle Bin (CTSK-333, Phase 3 — final phase of
// EPIC CTSK-385). List + restore + hard-purge soft-deleted rows across
// all 7 resource types without needing direct Postgres access.
// CTSK-513 added Notes (the 7th): same tab/list/restore/purge shell, driven by
// one RESOURCE_CONFIGS entry plus the optional `displayName` / `extraColumns`
// hooks below (a note has no title or slug).
//
// Structure mirrors AuditPanel.tsx (CTSK-389, Phase 4 — the sibling tab
// this one sits next to): self-contained panel, own queries, no
// SettingsEnvelope. Tab strip mirrors PatsPanel.tsx's "Revoked" tab
// pattern (CTSK-319) at the concept level (a filtered, opt-in view of
// otherwise-hidden rows), adapted to 6 mutually-exclusive resource-type
// tabs instead of PatsPanel's overlapping filter axes.
//
// Data notes:
//  - List reads use the Phase 2 (CTSK-388) `?deletedOnly=true` param on
//    each resource's existing list endpoint. As of CTSK-421, the
//    deletedOnly=true (and includeDeleted=true) branch of all 6 list
//    endpoints additionally requires `admin:view_deleted` — inline in
//    the route handler, on top of the resource's own `:view` cap, which
//    still gates the plain (live-only) listing. Restore uses each
//    resource's Phase 1 `:manage`-style cap (unchanged); Purge uses
//    `admin:purge_deleted` (unchanged).
//  - "deleted by" isn't a column on any of the 6 tables — it's derived
//    client-side from the most recent `soft_deleted` `resource_audit`
//    row per resource id (GET /audit?resourceType=X&eventType=
//    soft_deleted, sorted eventAt DESC server-side per audit.ts, so the
//    first row per id in the response is the most recent).

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import type { AuditEventRow, AuditResourceType } from '@captain-tasko/shared';
import { apiFetch, apiList, ApiError } from '../lib/api.js';
import { SkeletonRows } from './Skeleton.js';
import { toast } from '../components/Toast.js';
import { PurgeConfirmModal } from './PurgeConfirmModal.js';

interface RecycleRow {
  id: string;
  deletedAt: string | null;
  [key: string]: unknown;
}

interface ResourceConfig {
  key: AuditResourceType;
  label: string;
  /** Base REST path, e.g. '/tags'. Restore = `${apiPath}/:id/restore`
   *  (Phase 1); Purge = `${apiPath}/:id/actions/purge` (this ticket). */
  apiPath: string;
  /** Field the row's own natural key lives in — this is what the API's
   *  `confirmSlug` body is checked against server-side, so the modal
   *  must ask for exactly this value. */
  confirmFieldKey: string;
  confirmFieldLabel: string;
  /** What to show as the row's primary display name. Usually the same
   *  as confirmFieldKey; documents use `title` for both. */
  displayNameKey: string;
  /** CTSK-513 — override for resources with no natural name (a note's title
   *  is the first 60 characters of its body). */
  displayName?: (row: RecycleRow) => string;
  /** CTSK-513 — header for the primary column; defaults to the singular label. */
  nameHeader?: string;
  /** CTSK-513 — extra columns rendered after the primary column. */
  extraColumns?: { header: string; render: (row: RecycleRow) => ReactNode }[];
}

const RESOURCE_CONFIGS: ResourceConfig[] = [
  { key: 'document', label: 'Documents', apiPath: '/documents', confirmFieldKey: 'title', confirmFieldLabel: 'title', displayNameKey: 'title' },
  { key: 'tag', label: 'Tags', apiPath: '/tags', confirmFieldKey: 'name', confirmFieldLabel: 'name', displayNameKey: 'name' },
  { key: 'storage_container', label: 'Storage Containers', apiPath: '/storage-containers', confirmFieldKey: 'slug', confirmFieldLabel: 'slug', displayNameKey: 'name' },
  { key: 'tool', label: 'Tools', apiPath: '/tools', confirmFieldKey: 'slug', confirmFieldLabel: 'slug', displayNameKey: 'name' },
  { key: 'nest', label: 'Nests', apiPath: '/nests', confirmFieldKey: 'slug', confirmFieldLabel: 'slug', displayNameKey: 'name' },
  { key: 'custom_property', label: 'Custom Properties', apiPath: '/custom-properties', confirmFieldKey: 'key', confirmFieldLabel: 'key', displayNameKey: 'name' },
  // CTSK-520. File workflows have no slug; purge is confirmed by typing the
  // workflow's name. The list is `GET /file-workflows?deletedOnly=true`
  // (pipelines:view + admin:view_deleted); restore/purge gates are
  // pipelines:configure / admin:purge_deleted.
  { key: 'file_workflow', label: 'File Workflows', apiPath: '/file-workflows', confirmFieldKey: 'name', confirmFieldLabel: 'name', displayNameKey: 'name' },
  {
    // CTSK-513. Purge is confirmed by typing the note's id (notes have no
    // slug); the list is `GET /notes?deletedOnly=true` (admin:view_deleted).
    key: 'note',
    label: 'Notes',
    apiPath: '/notes',
    confirmFieldKey: 'id',
    confirmFieldLabel: 'id',
    displayNameKey: 'body',
    nameHeader: 'Title',
    displayName: (row) => noteTitle(typeof row.body === 'string' ? row.body : ''),
    extraColumns: [
      {
        header: 'Author',
        render: (row) => (typeof row.authorDisplayName === 'string' && row.authorDisplayName ? row.authorDisplayName : <em style={{ color: 'var(--text-muted)' }}>unknown</em>),
      },
      { header: 'Attached to', render: (row) => <NoteAttachedTo row={row} /> },
    ],
  },
];

/** First 60 characters of a note's body, single-lined and ellipsised. */
export function noteTitle(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length > 60 ? `${flat.slice(0, 60).trimEnd()}…` : flat;
}

function NoteAttachedTo({ row }: { row: RecycleRow }) {
  const type = row.targetType;
  const id = row.targetId;
  if (typeof type !== 'string' || typeof id !== 'string') return <>Standalone note</>;
  const path = type === 'document' ? `/documents/${id}` : `/workitems/${id}`;
  const title = typeof row.targetTitle === 'string' && row.targetTitle ? row.targetTitle : null;
  // A target the caller cannot resolve (gone, no view capability) still shows
  // what it was attached to, without a title.
  return title ? <a href={path}>{title}</a> : <span style={{ color: 'var(--text-muted)' }}>{type === 'document' ? 'a document' : 'a work item'} (title unavailable)</span>;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function str(row: RecycleRow, key: string): string {
  const v = row[key];
  return typeof v === 'string' ? v : String(v ?? '');
}

export function RecycleBinPanel() {
  const qc = useQueryClient();
  const [activeKey, setActiveKey] = useState<AuditResourceType>('document');
  const [filterText, setFilterText] = useState('');
  const [purgeTarget, setPurgeTarget] = useState<RecycleRow | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  const config = RESOURCE_CONFIGS.find((c) => c.key === activeKey)!;
  const nameOf = (row: RecycleRow): string => (config.displayName ? config.displayName(row) : str(row, config.displayNameKey));

  const listQ = useQuery({
    queryKey: ['recycle-bin', 'list', activeKey],
    queryFn: () => apiList<RecycleRow>(config.apiPath, { query: { deletedOnly: true, limit: 200 } }),
  });

  // Best-effort "deleted by" lookup. Failure here (e.g. caller lacks
  // audit:view even though they have admin:view_deleted — those are
  // deliberately separate caps) shouldn't block the recycle bin list
  // itself from rendering, so this query's error is swallowed below
  // rather than surfaced as a page-level failure.
  const deletedByQ = useQuery({
    queryKey: ['recycle-bin', 'deleted-by', activeKey],
    queryFn: () => apiList<AuditEventRow>('/audit', { query: { resourceType: activeKey, eventType: 'soft_deleted', limit: 200 } }),
    retry: false,
  });

  const deletedByMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of deletedByQ.data?.data ?? []) {
      if (map.has(row.resourceId)) continue; // first occurrence = most recent (server sorts eventAt DESC)
      const label = row.actor?.name ?? row.actor?.id ?? row.actorUserId ?? row.actorSourceTag ?? 'system';
      map.set(row.resourceId, label);
    }
    return map;
  }, [deletedByQ.data]);

  const restore = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`${config.apiPath}/${id}/restore`, { method: 'POST' }),
    onSuccess: () => {
      toast.success(`Restored — it's back in the live ${config.label.toLowerCase()} list.`);
      qc.invalidateQueries({ queryKey: ['recycle-bin', 'list', activeKey] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const purge = useMutation({
    mutationFn: ({ id, confirmSlug }: { id: string; confirmSlug: string }) =>
      apiFetch<void>(`${config.apiPath}/${id}/actions/purge`, { method: 'POST', body: { confirmSlug } }),
    onSuccess: () => {
      toast.success('Purged — permanently deleted.');
      setPurgeTarget(null);
      setPurgeError(null);
      qc.invalidateQueries({ queryKey: ['recycle-bin', 'list', activeKey] });
      qc.invalidateQueries({ queryKey: ['recycle-bin', 'deleted-by', activeKey] });
    },
    onError: (e: Error) => {
      setPurgeError(e.message);
    },
  });

  const rows = listQ.data?.data ?? [];
  const filteredRows = filterText.trim()
    ? rows.filter((r) => nameOf(r).toLowerCase().includes(filterText.trim().toLowerCase()))
    : rows;
  // Sort default: deleted_at DESC (most recently deleted first).
  const sortedRows = [...filteredRows].sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? ''));

  if (listQ.isError) {
    const err = listQ.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          Viewing the recycle bin requires the <code>admin:view_deleted</code> capability
          (in addition to {config.label.toLowerCase()}-view access — CTSK-421).
        </div>
      );
    }
    return <div style={{ color: 'var(--danger-primary, #B3261E)' }}>Failed to load {config.label.toLowerCase()}.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Recycle Bin</h2>
        </div>
        <p style={{ margin: '0 0 16px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
          Soft-deleted rows across all 7 resource types. Restore brings a row back to the live
          list. Purge hard-deletes it permanently — there is no undo, and no way to restore a
          purged row. Every restore and purge writes a <code>resource_audit</code> entry (see the
          Audit tab).
        </p>

        {/* ── Resource-type tabs ─────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {RESOURCE_CONFIGS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={clsx('btn', activeKey === c.key && 'btn--primary')}
              onClick={() => { setActiveKey(c.key); setFilterText(''); }}
              style={{ fontSize: 12, padding: '5px 12px' }}
            >
              {c.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder={`Filter by ${(config.nameHeader ?? config.confirmFieldLabel).toLowerCase()}…`}
          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, marginBottom: 14, width: 260 }}
        />

        {listQ.isLoading ? (
          <SkeletonRows rows={6} />
        ) : sortedRows.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>
            {filterText ? 'No soft-deleted rows match this filter.' : `No soft-deleted ${config.label.toLowerCase()}.`}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-subtle)' }}>
                <th style={{ padding: '6px 8px' }}>{config.nameHeader ?? config.label.slice(0, -1)}</th>
                {config.extraColumns?.map((c) => (
                  <th key={c.header} style={{ padding: '6px 8px' }}>{c.header}</th>
                ))}
                <th style={{ padding: '6px 8px' }}>Deleted at</th>
                <th style={{ padding: '6px 8px' }}>Deleted by</th>
                <th style={{ padding: '6px 8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <div>{nameOf(row) || <em>(untitled)</em>}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
                      {config.confirmFieldLabel !== config.displayNameKey && config.confirmFieldKey !== 'id' && str(row, config.confirmFieldKey)
                        ? `${config.confirmFieldLabel}: ${str(row, config.confirmFieldKey)} · `
                        : ''}
                      {row.id}
                    </div>
                  </td>
                  {config.extraColumns?.map((c) => (
                    <td key={c.header} style={{ padding: '6px 8px' }}>{c.render(row)}</td>
                  ))}
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                    {formatTimestamp(row.deletedAt)}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    {deletedByQ.isLoading ? '…' : deletedByMap.get(row.id) ?? <em style={{ color: 'var(--text-muted)' }}>unknown</em>}
                  </td>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn"
                      disabled={restore.isPending}
                      onClick={() => restore.mutate(row.id)}
                      style={{ fontSize: 12, padding: '4px 10px', marginRight: 6 }}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => { setPurgeTarget(row); setPurgeError(null); }}
                      style={{ fontSize: 12, padding: '4px 10px', color: 'var(--danger-primary, #B3261E)', borderColor: 'var(--danger-primary, #B3261E)' }}
                    >
                      Purge
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {purgeTarget && (
        <PurgeConfirmModal
          open
          resourceLabel={config.label.slice(0, -1).toLowerCase()}
          rowLabel={nameOf(purgeTarget) || purgeTarget.id}
          confirmValue={str(purgeTarget, config.confirmFieldKey)}
          isPending={purge.isPending}
          errorMessage={purgeError}
          onCancel={() => { setPurgeTarget(null); setPurgeError(null); }}
          onConfirm={() => purge.mutate({ id: purgeTarget.id, confirmSlug: str(purgeTarget, config.confirmFieldKey) })}
        />
      )}
    </div>
  );
}
